// Concierge module types - based on concierge_data_*.json structure

export interface SimpleTextParams {
  title: string;
  short_text_content: string;
  context?: string;
}

export interface WelcomeScreen {
  params_primary: { context: string };
  params_secondary: { title: string; short_text_content: string };
}

export interface Wayfinding {
  params_secondary: { short_text_content: string; context: string };
}

export interface VideoCall {
  params_secondary: SimpleTextParams;
}

export interface RobotStatus {
  params_secondary: SimpleTextParams;
}

export interface MenuItem {
  title: string;
  sub_title: string;
  image: [string, string]; // [icon_filename, background_filename]
}

export interface Menu {
  params_primary: { context: string };
  params_secondary: MenuItem[];
}

export interface HighlightCategory {
  title: string;
}

export interface HighlightItem {
  title: string;
  sub_title: string;
  short_text_content: string;
  image: string[];
  level?: number; // ACM only — not present in CCM highlights
  context: string;
}

export interface MuseumHighlights {
  params_primary?: HighlightCategory[]; // Optional — not present in all museums
  params_secondary: HighlightItem[];
}

export interface PricingTier {
  label?: string;
  price: string;
}

export interface PriceSection {
  title: string;
  sub_title?: string;
  pricing_tiers: PricingTier[];
  disclaimer?: string;
}

export interface AdmissionPrices {
  params_secondary: {
    title: string;
    prices: {
      sections: PriceSection[];
      footer_note?: string;
    };
    context: string;
  };
}

export interface GettingAroundItem {
  title: string;
  sub_title?: string;
  short_text_content?: string;
  image?: string[];
  context?: string;
}

export interface GettingAround {
  question: string;
  answers: string[];
  context: string;
  '0': { params_secondary: GettingAroundItem[] };
  '1': { params_primary: GettingAroundItem[]; params_secondary: GettingAroundItem[] };
}

// eat_drink_shop — ACM specific
export interface EatDrinkShopItem {
  title: string;
  short_text_content: string; // operating hours
  image: string[];
}

export interface EatDrinkShopSecondaryItem {
  title: string;
  short_text_content: string; // about description
  image: string[];
  context?: string;
}

export interface EatDrinkShop {
  params_primary: EatDrinkShopItem[];
  params_secondary: EatDrinkShopSecondaryItem[];
}

// donate — CCM specific
export interface DonateItem {
  title: string;
  short_text_content: string;
  image: string[];
  image_text?: string;
  context?: string;
}

export interface Donate {
  params_secondary: DonateItem[];
}

// contribute — shared
export interface Contribute {
  params_secondary: {
    title: string;
    short_text_content: string;
    image_text: string;
    image: string[];
    context?: string;
  };
}

// feedback — shared
export interface FeedbackQuestion {
  title: string;
  question: string;
  answers: string[];
  type: string; // e.g. "rating"
  context?: string;
}

export interface Feedback {
  params_primary: FeedbackQuestion[];
  params_secondary: {
    title: string;
    short_text_content: string;
    image_text: string;
    image: string[];
    context?: string;
  };
}

export interface ConciergeData {
  go_home_destination: string;
  welcome_screen: WelcomeScreen;
  wayfinding: Wayfinding;
  video_call: VideoCall;
  robot_is_moving: RobotStatus;
  robot_arrived: RobotStatus;
  robot_going_home: RobotStatus;
  robot_going_to_starting_point: RobotStatus;
  menu: Menu;
  museum_highlights: MuseumHighlights;
  admission_prices?: AdmissionPrices; // ACM only
  getting_around: GettingAround;
  eat_drink_shop?: EatDrinkShop; // ACM only
  donate?: Donate; // CCM only
  contribute: Contribute;
  feedback: Feedback;
}

export type ConciergeSection = keyof Omit<ConciergeData, 'go_home_destination'>;

export interface ConciergeSectionMeta {
  key: ConciergeSection;
  label: string;
  description: string;
}

export const CONCIERGE_SECTIONS: ConciergeSectionMeta[] = [
  { key: 'welcome_screen', label: 'Welcome Screen', description: 'Initial greeting when visitors approach the robot' },
  { key: 'wayfinding', label: 'Wayfinding', description: 'Museum map section labels and colours' },
  { key: 'video_call', label: 'Video Call', description: 'Staff assistance / video call screen' },
  { key: 'robot_is_moving', label: 'Robot Moving', description: 'Displayed while robot navigates to a destination' },
  { key: 'robot_arrived', label: 'Robot Arrived', description: 'Displayed when robot reaches its destination' },
  { key: 'robot_going_home', label: 'Robot Going Home', description: 'Displayed when robot returns to base' },
  {
    key: 'robot_going_to_starting_point',
    label: 'Robot Going to Start',
    description: 'Displayed when robot returns to starting point',
  },
  { key: 'menu', label: 'Main Menu', description: 'Concierge menu items and categories' },
  { key: 'museum_highlights', label: 'Museum Highlights', description: 'Featured exhibits and gallery items' },
  { key: 'admission_prices', label: 'Admission Prices', description: 'Ticket pricing tiers and information (ACM)' },
  { key: 'getting_around', label: 'Getting Around', description: 'Wayfinding options to other areas' },
  { key: 'eat_drink_shop', label: 'Eat, Drink & Shop', description: 'Dining and retail listings (ACM)' },
  { key: 'donate', label: 'Donate', description: 'Donation options and information (CCM)' },
  { key: 'contribute', label: 'Contribute', description: 'Stories and photographs contribution QR code' },
  { key: 'feedback', label: 'Feedback', description: 'Visitor feedback rating and QR code' },
];
